// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/architecture/python-fastapi-architecture.md

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload,
  Close,
} from '@mui/icons-material';
import { apiClient, API_ENDPOINTS } from '../config/api';
import { fetchVoiceUploadLanguages } from '../utils/voiceLanguageConfig';
import AlertDialog from './common/AlertDialog';
import DragDropZone from './common/DragDropZone';

// Language persistence storage key
const LANGUAGE_STORAGE_KEY = 'nexus_voice_upload_last_language';

const VoiceUploadModal = ({ open, onClose, onUpload, preSelectedFiles = [] }) => {
  const [selectedLanguage, setSelectedLanguage] = useState(import.meta.env.VITE_DEFAULT_LANGUAGE || "th");
  const [selectedCriteria, setSelectedCriteria] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCriteria, setLoadingCriteria] = useState(false);
  const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', severity: 'info' });
  const [showMultiFileWarning, setShowMultiFileWarning] = useState(false);

  // Load configuration files
  useEffect(() => {
    console.log('🔧 VoiceUploadModal mounted, calling loadConfigurations...');
    loadConfigurations();
  }, []);

  // Reset alert dialog state, load saved language, and handle pre-selected files when modal opens
  useEffect(() => {
    if (open) {
      setAlertDialog({ open: false, title: '', message: '', severity: 'info' });
      
      // Handle pre-selected files from drag & drop on main page
      if (preSelectedFiles && preSelectedFiles.length > 0) {
        setSelectedFiles(preSelectedFiles);
        // Show warning for multi-file upload
        if (preSelectedFiles.length > 1) {
          setShowMultiFileWarning(true);
        }
      } else {
        setSelectedFiles([]);
      }
      
      // Load saved language from localStorage
      try {
        const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        
        // Validate saved language exists in available languages
        if (savedLanguage && languages.length > 0) {
          const languageExists = languages.find(lang => lang.code === savedLanguage);
          if (languageExists) {
            setSelectedLanguage(savedLanguage);
            console.log(`✅ Loaded saved language: ${savedLanguage}`);
            return;
          }
        }
        
        // Fallback to default language
        const defaultLang = languages.find(lang => lang.default) || languages[0];
        if (defaultLang) {
          setSelectedLanguage(defaultLang.code);
          console.log(`ℹ️ Using default language: ${defaultLang.code}`);
        }
      } catch (error) {
        // Handle localStorage errors (e.g., private browsing mode)
        console.warn('⚠️ Could not load saved language:', error);
        const defaultLang = languages.find(lang => lang.default) || languages[0];
        if (defaultLang) {
          setSelectedLanguage(defaultLang.code);
        }
      }
    }
  }, [open, languages, preSelectedFiles]);

  const loadConfigurations = async () => {
    try {
      console.log('🚀 Starting loadConfigurations...');
      
      // Load languages: API catalog first (matches server), then static JSON fallback
      console.log('📁 Loading language config...');
      const langList = await fetchVoiceUploadLanguages();
      setLanguages(langList);

      const defaultLang = langList.find((lang) => lang.default);
      if (defaultLang && !open) {
        setSelectedLanguage(defaultLang.code);
      }

      // Load QA criteria from API
      setLoadingCriteria(true);
      try {
        console.log('🔍 Loading QA criteria from API...');
        console.log('🔑 API Client base URL:', apiClient.defaults.baseURL);
        console.log('🔑 Auth token in localStorage:', localStorage.getItem('auth_token') ? 'Present' : 'Missing');
        
        const criteriaResponse = await apiClient.get(API_ENDPOINTS.QA_CRITERIA_VOICE_PROCESSING);
        console.log('✅ QA criteria API response:', criteriaResponse.data);
        const criteriaList = criteriaResponse.data.criteria || [];
        console.log('📋 QA criteria list:', criteriaList);
        setCriteria(criteriaList);
        
        // Set default criteria (first active one)
        const defaultCriteria = criteriaList.find(c => c.is_default && c.is_active);
        if (defaultCriteria) {
          console.log('🎯 Setting default criteria:', defaultCriteria.name);
          setSelectedCriteria(defaultCriteria.id);
        } else if (criteriaList.length > 0) {
          console.log('🎯 Setting first criteria:', criteriaList[0].name);
          setSelectedCriteria(criteriaList[0].id);
        } else {
          console.log('⚠️ No QA criteria found');
        }
      } catch (error) {
        console.error('❌ Failed to load QA criteria from API:', error);
        console.error('❌ Error details:', error.response?.data);
        // Fallback: allow upload without criteria
        setCriteria([]);
      } finally {
        setLoadingCriteria(false);
      }
      
    } catch (error) {
      console.error('Failed to load configurations:', error);
      // Fallback to default values
      setLanguages([
        { code: "th", name: "Thai (ไทย)", default: true },
        { code: "en", name: "English", default: false },
        { code: "zh", name: "Chinese (中文)", default: false }
      ]);
    }
  };

  // Handle file selection (from both drag & drop and file input)
  const handleFileSelect = (files) => {
    // If files is an event object (from file input), extract files
    const newFiles = files instanceof Event ? Array.from(files.target.files) : files;
    
    // Combine with existing files (for "Add More" functionality)
    const combinedFiles = [...selectedFiles, ...newFiles];
    
    // Validate max 5 files
    if (combinedFiles.length > 5) {
      setAlertDialog({
        open: true,
        title: 'Too Many Files',
        message: `Maximum 5 files allowed. You have ${selectedFiles.length} file(s) already selected. You can add ${5 - selectedFiles.length} more file(s).`,
        severity: 'error'
      });
      if (files instanceof Event) {
        files.target.value = null;
      }
      return;
    }
    
    setSelectedFiles(combinedFiles);
    
    // Show warning for multi-file upload
    if (combinedFiles.length > 1) {
      setShowMultiFileWarning(true);
    }
    
    // Reset input to allow selecting same file again
    if (files instanceof Event) {
      files.target.value = null;
    }
  };

  // Handle language change with persistence
  const handleLanguageChange = (event) => {
    const newLanguage = event.target.value;
    setSelectedLanguage(newLanguage);
    
    // Save to localStorage
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLanguage);
      console.log(`💾 Saved language preference: ${newLanguage}`);
    } catch (error) {
      // Handle localStorage errors gracefully
      console.warn('⚠️ Could not save language preference:', error);
      // Continue with language change even if save fails
    }
  };

  const handleUploadClick = () => {
    if (selectedFiles.length === 0) return;
    
    // Show warning for multi-file uploads
    if (selectedFiles.length > 1) {
      setShowMultiFileWarning(true);
    } else {
      handleUpload();
    }
  };
  
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setShowMultiFileWarning(false);
    setLoading(true);
    
    try {
      // Validate all files
      let totalSize = 0;
      const allowedTypes = ['audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/m4a', 'audio/flac', 'audio/ogg', 'audio/x-wav', 'audio/wave'];
      const allowedExtensions = ['.wav', '.mp3', '.m4a', '.flac', '.ogg', '.wma', '.aac', '.opus'];
      
      for (const file of selectedFiles) {
        if (!file.name) {
          throw new Error('Invalid file: No filename provided');
        }
        
        // Check file size (max 50MB per file)
        if (file.size > 50 * 1024 * 1024) {
          throw new Error(`File too large: ${file.name} (${Math.round(file.size / 1024 / 1024)}MB). Maximum: 50MB per file.`);
        }
        
        totalSize += file.size;
        
        // Check file type
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        const hasValidMimeType = allowedTypes.includes(file.type);
        const hasValidExtension = allowedExtensions.includes(fileExtension);
        
        if (!hasValidMimeType && !hasValidExtension) {
          throw new Error(`Unsupported file type: ${file.name}. Please upload audio files only.`);
        }
      }
      
      // Check total size (max 250MB for 5 files: 5 × 50MB)
      if (totalSize > 250 * 1024 * 1024) {
        throw new Error(`Total file size (${Math.round(totalSize / 1024 / 1024)}MB) exceeds 250MB limit.`);
      }
      
      // Create form data with all files
      const formData = new FormData();
      
      // Append all files
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      formData.append('language', selectedLanguage);
      
      if (selectedCriteria) {
        formData.append('qa_criteria_id', selectedCriteria);
      }
      
      console.log(`Uploading ${selectedFiles.length} file(s) as single session...`);
      
      // Upload all files in one request
      const response = await apiClient.post(API_ENDPOINTS.VOICE_UPLOAD, formData);
      const result = response.data;
      
      // Show success
      setAlertDialog({
        open: true,
        title: 'Upload Successful',
        message: `${selectedFiles.length} file(s) uploaded successfully and queued for processing.`,
        severity: 'success'
      });
      
      // Call callback
      await onUpload(result);
      
      // Reset form but don't close modal yet - wait for user to acknowledge success
      setSelectedFiles([]);
      setSelectedLanguage("th");
      const defaultCriteria = criteria.find(c => c.is_default && c.is_active);
      setSelectedCriteria(defaultCriteria ? defaultCriteria.id : (criteria[0]?.id || ""));
      
    } catch (error) {
      console.error('Upload failed:', error);
      setAlertDialog({
        open: true,
        title: 'Upload Failed',
        message: error.response?.data?.detail || error.message || 'Upload failed',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedFiles([]);
      setSelectedLanguage("th");
      // Reset to default criteria
      const defaultCriteria = criteria.find(c => c.is_default && c.is_active);
      setSelectedCriteria(defaultCriteria ? defaultCriteria.id : (criteria[0]?.id || ""));
      // Reset alert dialog state
      setAlertDialog({ open: false, title: '', message: '', severity: 'info' });
      onClose();
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Upload Voice Files</Typography>
          <Button
            onClick={handleClose}
            disabled={loading}
            startIcon={<Close />}
            size="small"
          >
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* Language Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Language</InputLabel>
            <Select
              value={selectedLanguage}
              onChange={handleLanguageChange}
              label="Language"
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* QA Criteria Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>QA Criteria</InputLabel>
            <Select
              value={selectedCriteria}
              onChange={(e) => setSelectedCriteria(e.target.value)}
              label="QA Criteria"
              disabled={loadingCriteria}
            >
              {loadingCriteria ? (
                <MenuItem value="" disabled>
                  <Typography variant="body2" color="text.secondary">
                    Loading QA criteria...
                  </Typography>
                </MenuItem>
              ) : criteria.length === 0 ? (
                <MenuItem value="" disabled>
                  <Typography variant="body2" color="text.secondary">
                    No QA criteria available
                  </Typography>
                </MenuItem>
              ) : (
                criteria.map((criterion) => (
                  <MenuItem 
                    key={criterion.id} 
                    value={criterion.id}
                    disabled={!criterion.is_active}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body1" fontWeight="bold">
                          {criterion.name}
                        </Typography>
                        {criterion.is_active ? (
                          criterion.is_default && (
                            <Chip label="Default" size="small" color="primary" sx={{ ml: 1 }} />
                          )
                        ) : (
                          <Chip label="Inactive" size="small" sx={{ ml: 1 }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {criterion.team_name} • {criterion.total_metrics} metrics
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* File Selection with Drag & Drop */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select Voice Files
            </Typography>
            
            <DragDropZone
              onFilesSelected={handleFileSelect}
              accept="audio/*"
              multiple={true}
              maxFiles={5}
              maxSize={50}
              disabled={loading}
            >
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CloudUpload 
                  sx={{ 
                    fontSize: 48, 
                    color: 'primary.main', 
                    mb: 2,
                    transition: 'transform 0.2s ease-in-out',
                  }} 
                />
                <Typography variant="h6" gutterBottom>
                  Drop files here or click to browse
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Supported formats: WAV, MP3, M4A, FLAC, OGG
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Maximum 5 files, 50MB per file
                </Typography>
              </Box>
            </DragDropZone>

            {/* Selected Files Display */}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Selected Files ({selectedFiles.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      size="small"
                      onDelete={() => removeFile(index)}
                      disabled={loading}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Loading Indicator */}
          {loading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Uploading files...
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleUploadClick}
          variant="contained"
          disabled={selectedFiles.length === 0 || loading}
          startIcon={<CloudUpload />}
        >
          Upload Files
        </Button>
      </DialogActions>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => {
          setAlertDialog({ open: false, title: '', message: '', severity: 'info' });
          // Close modal if this was a success message
          if (alertDialog.severity === 'success') {
            onClose();
          }
        }}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
      
      {/* Multi-File Warning Dialog */}
      <Dialog open={showMultiFileWarning} onClose={() => setShowMultiFileWarning(false)}>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          ⚠️ Multi-File Upload Confirmation
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            You are uploading <strong>{selectedFiles.length} files</strong> as a single analysis session.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Important Guidelines:</strong>
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li><Typography variant="body2">Maximum <strong>5 files</strong> per session</Typography></li>
              <li><Typography variant="body2">All files must be about the <strong>same subject/conversation</strong></Typography></li>
              <li><Typography variant="body2">All files must be in the <strong>same language</strong> ({selectedLanguage.toUpperCase()})</Typography></li>
              <li><Typography variant="body2">Files will be analyzed together for accurate results</Typography></li>
            </ul>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Files to upload: {selectedFiles.map(f => f.name).join(', ')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMultiFileWarning(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained" color="primary">
            Confirm & Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default VoiceUploadModal;
